import Countdown from "react-countdown";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { Flex, Image, Text } from "rebass/styled-components";
import { useRecoilState, useSetRecoilState } from "recoil";
import { RiCheckLine } from "react-icons/ri";

import { TComp } from "MVVM/Views/Competitions/@types";
import { CountDownRenderer } from "MVVM/Views/Competitions/Views/Play/countdownTimer";
import { ClipSelectorViewModel } from "./ViewModel";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { Button } from "MVVM/Components/Button";
import { analytics } from "MVVM/Analytics";
import { COTDSubmitted } from "../COTDSubmitted";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { SubmittedClips } from "MVVM/State/campaign";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";

interface Clip {
  _id: string;
  clipImageThumb: string;
  clipTitle: string;
  createdDate: string;
  score: {
    value: number;
  };
}

const LIMIT = 10;

export const ClipSelector = ({
  campaign,
  closeModal,
}: {
  campaign: TComp;
  closeModal: () => void;
}) => {
  const { competitionClips, loading } = ClipSelectorViewModel({
    cardIds: campaign.creatorCards,
  });
  const dontShowKey = `dont_show_cotd_${campaign.campaign}`;
  const dontShow = window.localStorage.getItem(dontShowKey);
  const [selectedClips, setSelectedClips] = useState<Clip[]>([]);
  const [finished, setFinished] = useState(false);
  const [submittedClips, setSubmittedClips] = useRecoilState(SubmittedClips);
  const setAllstarModalState = useSetRecoilState(AllstarModalState);

  useEffect(() => {
    if (loading) return;
    analytics.track("Component Viewed", {
      locationIdentifier: "cotd_clip_selector",
      location: "cotd_widget",
      campaign: campaign.campaign,
      hasClips: competitionClips?.length > 0,
      featureGroup: "cotd_widget",
    });
  }, [campaign, loading, competitionClips?.length]);

  const onClose = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      window.localStorage.setItem(dontShowKey, "true");
    }
    closeModal();
  };

  if (finished) {
    if (dontShow) onClose(true);
    return <COTDSubmitted campaign={campaign} onClose={onClose} />;
  }

  return (
    <Flex
      flexDirection="column"
      backgroundColor="darkerNed"
      style={{ borderRadius: 12 }}
    >
      <CampaignContainer backgroundImage={campaign.images.carouselBackground}>
        <TimerColumn>
          <CampaignImage src={campaign.images.campaign} />
          <Countdown
            date={new Date(parseInt(campaign.campaignJoinEnd))}
            renderer={(props) => (
              <CountDownRenderer
                days={props.days}
                hours={props.hours}
                minutes={props.minutes}
                seconds={props.seconds}
                color={campaign.color}
                variant="text.labelMedium"
                showEndsIn={false}
                showIcon={true}
              />
            )}
          />
        </TimerColumn>
        <TextColumn>
          <Text variant="text.headingXSmall" color={"ice"}>
            Enter Your Clips
          </Text>
          <Text variant="text.paragraphMedium" color={"chalk"}>
            Choose up to {LIMIT} clips at once to enter into the Clip Of The Day
            competition. You may enter as many clips as you like, but only one
            can win.
          </Text>

          <Text
            variant="text.labelXSmall"
            color={"error"}
            style={{
              fontWeight: "normal",
            }}
          >
            Clips older than 1 day are not eligible for the{" "}
            {campaign?.partnerName ?? ""} COTD competition and will not display
            below.
          </Text>
        </TextColumn>
      </CampaignContainer>
      {!loading && !competitionClips?.length && (
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          style={{ gap: 8, minHeight: 400, maxHeight: 400 }}
          width="100%"
        >
          <Text variant="text.headingXSmall" color={"shade400"}>
            No Eligible Clips
          </Text>

          <Text
            variant="text.paragraphMedium"
            color={"shade400"}
            style={{ fontWeight: 400, maxWidth: 400, textAlign: "center" }}
          >
            In order to submit clips to this Clip of the Day Competition, enable
            the {campaign?.partnerName ?? ""} Signature!
          </Text>
          <EnableSignatureButton
            classification="secondaryRounded"
            flex={true}
            mt={4}
            onClick={() => {
              analytics.track("Button - Click", {
                locationIdentifier: "enable_signature",
                location: "cotd_clip_selector",
                campaign: campaign.campaign,
                featureGroup: "cotd_widget",
              });
              setAllstarModalState({
                isOpen: ModalType.SignatureSelector,
                data: {
                  preselectTheme: campaign.signature.themeName,
                  preselectStyle: campaign.signature.styleName,
                },
                functions: {},
              });
              closeModal();
            }}
          >
            <Image
              src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
              height={16}
              width={16}
            />
            <Text variant="text.labelXSmall" color={"ice"}>
              Enable Signature
            </Text>
          </EnableSignatureButton>
        </Flex>
      )}

      {loading && (
        <ClipsContainer>
          {Array.from({ length: 10 }).map((_, index) => (
            <>
              <Flex flexDirection="column" style={{ gap: 8 }}>
                <LoadingSkeleton key={index} style={{ minHeight: 100 }} />
                <LoadingSkeleton
                  key={index}
                  style={{ minHeight: 15, width: "50%" }}
                />
                <LoadingSkeleton
                  key={index}
                  style={{ minHeight: 10, width: "30%" }}
                />
              </Flex>
            </>
          ))}
        </ClipsContainer>
      )}

      {!!competitionClips?.length && (
        <ClipsContainer>
          {competitionClips?.map((clip: Clip) => (
            <ClipItem key={clip._id}>
              <ClipThumbnail
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/${clip.clipImageThumb}`}
                alt={clip.clipTitle}
                color={campaign.color}
                selected={selectedClips.some((c) => c._id === clip._id)}
                submitted={submittedClips.includes(clip._id)}
                onClick={() => {
                  if (submittedClips.includes(clip._id)) return;
                  if (selectedClips.some((c) => c._id === clip._id)) {
                    setSelectedClips(
                      selectedClips.filter((c) => c._id !== clip._id),
                    );
                  } else {
                    if (selectedClips.length < LIMIT) {
                      setSelectedClips([...selectedClips, clip]);
                    }
                  }
                }}
              />
              {submittedClips.includes(clip._id) && (
                <CheckmarkContainer color={campaign.color}>
                  <CheckmarkIcon color={campaign.color} />
                  <Text color={campaign.color} variant="text.labelXXSmall">
                    Submitted!
                  </Text>
                </CheckmarkContainer>
              )}
              <Flex
                flexDirection="column"
                alignItems="start"
                style={{ width: "100%", gap: 8, marginTop: 16 }}
              >
                <Text variant="text.labelMedium" color="ice">
                  {clip.clipTitle}
                </Text>
                <Text variant="text.labelSmall" color="chalk">
                  {dateFormatWithDistanceSuffix(clip.createdDate)}
                </Text>
              </Flex>
            </ClipItem>
          ))}
        </ClipsContainer>
      )}

      <ControlsFooter>
        <NumSelectedText color={campaign.color} variant="text.headingXSmall">
          {selectedClips.length} Selected
        </NumSelectedText>
        <ButtonsContainer>
          <Button
            classification="secondaryRounded"
            flex={true}
            style={{
              minWidth: 80,
              minHeight: 50,
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => {
              onClose(false);
            }}
          >
            Cancel
          </Button>
          <StylePrimaryButton
            classification="roundedFilled"
            flex={true}
            bgColor={campaign.color}
            style={{
              minWidth: 80,
              minHeight: 50,
              alignItems: "center",
              justifyContent: "center",
            }}
            disabled={selectedClips.length === 0}
            onClick={() => {
              const clipIds = selectedClips.map((clip) => clip._id);
              setSubmittedClips([...new Set([...submittedClips, ...clipIds])]);
              sendToRudderstack({
                selectedClips,
                campaignName: campaign.campaign,
              });
              setFinished(true);
            }}
          >
            Enter
          </StylePrimaryButton>
        </ButtonsContainer>
      </ControlsFooter>
    </Flex>
  );
};

const sendToRudderstack = ({
  selectedClips,
  campaignName,
}: {
  selectedClips: Clip[];
  campaignName: string;
}) => {
  for (const selectedClip of selectedClips) {
    analytics.track("cotd_submitted", {
      clipId: selectedClip._id,
      clipUrl: `${window.location.origin}/clip/${selectedClip._id}`,
      score: selectedClip.score?.value,
      campaignName,
    });
  }
};

const EnableSignatureButton = styled(Button)`
  min-width: 140px;
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkerNavy};
  }
`;

const NumSelectedText = styled(Text)<{ color: string }>`
  color: ${({ color }) => color};
`;

const StylePrimaryButton = styled(Button)<{ bgColor: string }>`
  background-color: ${({ bgColor }) => bgColor};
  &:hover {
    background-color: ${({ bgColor }) => bgColor}dd;
  }
`;

const ButtonsContainer = styled(Flex)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ControlsFooter = styled(Flex)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.ned};
  min-height: 100px;
  gap: 16px;
`;

const ClipsContainer = styled(Flex)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  gap: 24px;
  padding: 24px;
  min-height: 400px;
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
`;

const ClipItem = styled(Flex)`
  flex-direction: column;
  max-height: 175px;
  align-items: center;
  text-align: center;
  position: relative;
`;

const ClipThumbnail = styled(Image)<{
  color: string;
  selected: boolean;
  submitted: boolean;
}>`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
  cursor: ${({ submitted }) => (submitted ? "not-allowed" : "pointer")};
  opacity: ${({ submitted }) => (submitted ? 0.3 : 1)};
  outline: ${({ selected, submitted, color }) =>
    selected || submitted ? `2px solid ${color}` : "none"};

  &:hover {
    outline: ${({ color }) => `2px solid ${color}`};
  }
`;

const TextColumn = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;

const CampaignContainer = styled(Flex)<{ backgroundImage: string }>`
  flex-direction: row;
  gap: 48px;
  justify-content: center;
  padding-top: 24px;
  padding-bottom: 24px;
  padding-left: 24px;
  padding-right: 24px;
  border-radius: 12px;
  background-image: url(${({ backgroundImage }) => backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
`;

const TimerColumn = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  max-width: 150px;
`;

const CampaignImage = styled(Image)`
  width: 125px;
  height: auto;
`;

const CheckmarkContainer = styled(Flex)<{ color: string }>`
  position: absolute;
  top: 8px;
  right: 8px;
  align-items: center;
  gap: 2px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 16px;
  padding: 4px 8px;
`;

const CheckmarkIcon = styled(RiCheckLine)<{ color: string }>`
  color: ${({ color }) => color};
  width: 15px;
  height: 15px;
`;
